import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from "react-router-dom";
import axios from 'axios';
import cookie from 'react-cookies';
import { LogService } from "./LogService";
import CachedTenantSettingsRepository from "../CachedTenantSettingsRepository";
import { authenticatedSelector, changeAuthenticated, changeDoLogout, changeEnvName, changeRefreshLoggedInUser, changeTenant, changeTenantSettings, changeUser, doLogoutSelector, envNameSelector, loggedInUserSelector, refreshLoggedInUserSelector, selectedTenantSelector } from "../store/slice/auth";
import { useAppDispatch, useAppSelector } from "../store";
import AUTH from "../constants/Auth";
/**
 * Renderless component that listens for authentication changes and then loads/unloads user details
 *
 * Also adds an axios interceptor to watch for 401 Unauthorised responses and triggers a redirect to login page.
 */
const NavToLoginPage = () => {
    const location = useLocation();
    return (_jsx(Navigate, { to: `/login?toUrl=${location.pathname + location.search}`, replace: true }));
};
const AuthenticationManager = () => {
    const [stateDoNavToLoginPage, setStateDoNavToLoginPage] = useState(false);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const envName = useAppSelector(envNameSelector);
    const authenticated = useAppSelector(authenticatedSelector);
    const doLogout = useAppSelector(doLogoutSelector);
    const refreshLoggedInUser = useAppSelector(refreshLoggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const dispatch = useAppDispatch();
    useEffect(() => {
        const userAuthCookie = cookie.load(AUTH.userAuthCookieName);
        if (userAuthCookie !== undefined && loggedInUser === null) {
            onLogin();
        }
        const cookieEnvName = cookie.load(AUTH.envNameCookieName);
        if (cookieEnvName && !envName) {
            dispatch(changeEnvName(cookieEnvName));
        }
    }, []);
    useEffect(() => {
        if (authenticated) {
            onLogin();
        }
    }, [authenticated]);
    useEffect(() => {
        if (doLogout) {
            onLogout();
            // reset for next time
            dispatch(changeDoLogout(false));
            dispatch(changeAuthenticated(false));
        }
    }, [doLogout]);
    useEffect(() => {
        if (refreshLoggedInUser) {
            dispatch(changeRefreshLoggedInUser(false));
            onRefreshLoggedInUser();
        }
    }, [refreshLoggedInUser]);
    useEffect(() => {
        if (stateDoNavToLoginPage) {
            // Reset doNavToLoginPage value for next logout
            setStateDoNavToLoginPage(false);
        }
    }, [stateDoNavToLoginPage]);
    useEffect(() => {
        onTenantChanged();
    }, [selectedTenant?.id]);
    const onLogout = () => {
        cookie.remove(AUTH.userAuthCookieName);
        cookie.remove(AUTH.shouldSetup2FACookieName);
        axios.post('/admin/v1/authentication/logout');
        setStateDoNavToLoginPage(true);
        dispatch(changeUser(null));
        dispatch(changeTenant(null));
    };
    const onLogin = () => {
        onRefreshLoggedInUser();
    };
    const onRefreshLoggedInUser = () => {
        axios.get('/admin/v1/users/me?includeRoles=true&includePermissions=true&includeTenant=true').then(response => {
            const loggedInUser = response.data;
            dispatch(changeUser(loggedInUser));
            dispatch(changeAuthenticated(true));
            LogService.setLoggedInUser(loggedInUser);
            if (loggedInUser.tenants && loggedInUser.tenants.length === 1) {
                dispatch(changeTenant(loggedInUser.tenants[0]));
            }
        });
    };
    const onTenantChanged = () => {
        if (selectedTenant?.id) {
            CachedTenantSettingsRepository.refreshSettingsForTenant(selectedTenant?.id)
                .then(tenantSettings => {
                dispatch(changeTenantSettings(tenantSettings));
            });
        }
        else { // No tenant selected
            dispatch(changeTenantSettings(null));
        }
    };
    if (stateDoNavToLoginPage) {
        return _jsx(NavToLoginPage, {});
    }
    return null;
};
export default AuthenticationManager;
